import { BUSINESS_TZ, zonedParts } from '@natashu/shared';

/** Shared formatters are the single source of truth; import them from here or from @natashu/shared. */
export {
  BUSINESS_TZ,
  bahtToMinor,
  formatMinor,
  formatRelativeThai,
  formatThaiDate,
  formatThaiDateTime,
  maskPhone,
} from '@natashu/shared';

type DateInput = Date | string | number;

function toDate(value: DateInput): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fullDate = new Intl.DateTimeFormat('th-TH-u-ca-buddhist', { dateStyle: 'full', timeZone: BUSINESS_TZ });
const fullDateTime = new Intl.DateTimeFormat('th-TH-u-ca-buddhist', { dateStyle: 'full', timeStyle: 'short', timeZone: BUSINESS_TZ });
const numberFormat = new Intl.NumberFormat('th-TH');

/** "วันจันทร์ที่ 14 กันยายน พ.ศ. 2569" (Bangkok time). */
export function formatThaiFullDate(value: DateInput): string {
  const d = toDate(value);
  return d ? fullDate.format(d) : '';
}

/** "วันจันทร์ที่ 14 กันยายน พ.ศ. 2569 เวลา 18:00 น." (Bangkok time). */
export function formatThaiFullDateTime(value: DateInput): string {
  const d = toDate(value);
  return d ? `${fullDateTime.format(d)} น.` : '';
}

export function formatNumber(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : numberFormat.format(value);
}

/** `null` means the channel cannot report it — shown as N/A, never 0%. */
export function formatPercent(ratio: number | null | undefined, fractionDigits = 1): string {
  if (ratio == null || !Number.isFinite(ratio)) return 'N/A';
  return `${(ratio * 100).toFixed(fractionDigits)}%`;
}

export function formatDurationThai(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return '—';
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s} วินาที`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} นาที`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} ชม. ${m % 60} นาที` : `${h} ชม.`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} วัน ${h % 24} ชม.` : `${d} วัน`;
}

/** Greeting by the current hour in the business time zone. */
export function thaiGreeting(now: Date = new Date()): string {
  const hour = zonedParts(now, BUSINESS_TZ).hour;
  if (hour >= 5 && hour < 12) return 'สวัสดีตอนเช้า';
  if (hour >= 12 && hour < 17) return 'สวัสดีตอนบ่าย';
  if (hour >= 17 && hour < 21) return 'สวัสดีตอนเย็น';
  return 'สวัสดี';
}
